import { useState, useEffect, useCallback } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Store, Users, Package, Tags, Boxes, Truck,
  FileText, ShoppingCart, BarChart3, Settings, LogOut, Menu, X, ChevronDown,
  Sun, Moon, ArrowLeftRight, ClipboardList, Gift, UtensilsCrossed, ChefHat,
  Percent, Tag, Clock, ShoppingBag, DollarSign, Check, ClipboardCheck, Calendar,
  Target, TrendingDown, ArrowRightLeft, Shield, MonitorSmartphone, TrendingUp, Receipt,
  Settings2, Headphones, ArrowLeft, BookOpen, Sparkles, CreditCard, QrCode, Megaphone, ArrowRight, Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator
} from "@/components/ui/dropdown-menu";
import {
  Collapsible, CollapsibleContent, CollapsibleTrigger
} from "@/components/ui/collapsible";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { useAuthStore, useThemeStore, api } from "@/store/useStore";
import { cn } from "@/lib/utils";

const navItems = [
  { title: "Dashboard", icon: LayoutDashboard, href: "/admin/dashboard" },
  { title: "Setup Wizard", icon: Sparkles, href: "/admin/setup-wizard" },
  {
    title: "Organization",
    icon: Building2,
    children: [
      { title: "Properties", icon: Building2, href: "/admin/properties" },
      { title: "Outlets", icon: Store, href: "/admin/outlets" },
      { title: "Stores", icon: Boxes, href: "/admin/stores" },
      { title: "Accounting Periods", icon: Calendar, href: "/admin/accounting-periods" },
    ],
  },
  {
    title: "Users",
    icon: Users,
    children: [
      { title: "All Users", icon: Users, href: "/admin/users" },
      { title: "User Roles", icon: Shield, href: "/admin/user-roles" },
    ],
  },
  {
    title: "Products",
    icon: Package,
    children: [
      { title: "All Products", icon: Package, href: "/admin/products" },
      { title: "Categories", icon: Tags, href: "/admin/categories" },
    ],
  },
  {
    title: "Inventory",
    icon: Boxes,
    children: [
      { title: "Purchase Orders", icon: FileText, href: "/admin/purchase-orders" },
      { title: "Goods Received", icon: ClipboardList, href: "/admin/grn" },
      { title: "Requisitions", icon: ArrowLeftRight, href: "/admin/requisitions" },
      { title: "Issues", icon: ArrowRightLeft, href: "/admin/store-issues" },
      { title: "Cost Centres", icon: Target, href: "/admin/cost-centres" },
      { title: "Item Reductions", icon: TrendingDown, href: "/admin/item-reductions" },
      { title: "Stock Takes", icon: ClipboardCheck, href: "/admin/stock-takes" },
      { title: "Stock Levels", icon: Boxes, href: "/admin/inventory" },
      { title: "Stock Valuation", icon: TrendingUp, href: "/admin/stock-valuation" },
      { title: "Suppliers", icon: Truck, href: "/admin/suppliers" },
    ],
  },
  {
    title: "POS",
    icon: MonitorSmartphone,
    children: [
      { title: "Shifts", icon: Clock, href: "/admin/shifts" },
      { title: "Orders", icon: ShoppingCart, href: "/admin/orders" },
      { title: "Online Orders", icon: ShoppingBag, href: "/admin/online-orders", badge: "NEW" },
      { title: "Tables", icon: UtensilsCrossed, href: "/admin/tables" },
      { title: "Customers", icon: Gift, href: "/admin/customers" },
      { title: "Devices", icon: MonitorSmartphone, href: "/admin/devices" },
    ],
  },
  {
    title: "Finance",
    icon: Percent,
    children: [
      { title: "Payments", icon: DollarSign, href: "/admin/payments" },
      { title: "eTIMS", icon: Receipt, href: "/admin/etims" },
      { title: "Taxes", icon: Percent, href: "/admin/taxes" },
      { title: "Discounts", icon: Tag, href: "/admin/discounts" },
    ],
  },
  { title: "Reports", icon: BarChart3, href: "/admin/reports" },
  { title: "Outlet Performance", icon: TrendingUp, href: "/admin/outlet-performance" },
  { title: "Fiscal Reports", icon: FileText, href: "/admin/fiscal-reports" },
  { title: "EOD Reports", icon: FileText, href: "/admin/eod-reports" },
  { title: "Settings", icon: Settings, href: "/admin/settings" },
  { title: "Online Store", icon: Globe, href: "/admin/online-store", badge: "NEW" },
  { title: "Subscription", icon: CreditCard, href: "/admin/subscription" },
  { title: "Help & Docs", icon: BookOpen, href: "/help?tab=releases", external: true },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const { user, logout, token } = useAuthStore();
  const { theme, toggleTheme } = useThemeStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState([]); // All menus collapsed by default
  
  // Property and Outlet selection state
  const [properties, setProperties] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [selectedOutletId, setSelectedOutletId] = useState("");
  const [loading, setLoading] = useState(true);
  
  // Release notes notification badge
  const [releaseUnreadCount, setReleaseUnreadCount] = useState(0);
  
  // "What's New" popup modal
  const [whatsNewOpen, setWhatsNewOpen] = useState(false);
  const [latestRelease, setLatestRelease] = useState(null);

  useEffect(() => {
    fetchPropertiesAndOutlets();
    fetchReleaseUnreadCount();
    checkWhatsNew();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPropertiesAndOutlets = async () => {
    try {
      setLoading(true);
      const [propsData, outletsData] = await Promise.all([
        api.get("/properties"),
        api.get("/outlets")
      ]);
      
      setProperties(propsData || []);
      setOutlets(outletsData || []);
      
      // Auto-select first property/outlet if only one exists
      if (propsData?.length === 1) {
        setSelectedPropertyId(propsData[0].id);
      } else if (propsData?.length > 0) {
        // Check if there's a saved selection in localStorage
        const savedPropId = localStorage.getItem("selectedPropertyId");
        if (savedPropId && propsData.find(p => p.id === savedPropId)) {
          setSelectedPropertyId(savedPropId);
        } else {
          setSelectedPropertyId(propsData[0].id);
        }
      }
      
      if (outletsData?.length === 1) {
        setSelectedOutletId(outletsData[0].id);
      } else if (outletsData?.length > 0) {
        const savedOutletId = localStorage.getItem("selectedOutletId");
        if (savedOutletId && outletsData.find(o => o.id === savedOutletId)) {
          setSelectedOutletId(savedOutletId);
        } else {
          setSelectedOutletId(outletsData[0].id);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchReleaseUnreadCount = async () => {
    try {
      const data = await api.get("/release-notes/unread-count");
      setReleaseUnreadCount(data?.unread_count || 0);
    } catch (error) {
      // Silently fail — non-critical
    }
  };

  const checkWhatsNew = async () => {
    try {
      const notes = await api.get("/release-notes");
      if (!notes?.length) return;
      const latest = notes[0]; // sorted by released_at desc
      const userId = user?.id || "anon";
      const dismissKey = `hostpoint_whats_new_dismissed_${userId}`;
      const dismissedVersion = localStorage.getItem(dismissKey);
      
      if (dismissedVersion !== latest.version) {
        setLatestRelease(latest);
        // Small delay so dashboard loads first
        setTimeout(() => setWhatsNewOpen(true), 1200);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const dismissWhatsNew = () => {
    setWhatsNewOpen(false);
    if (latestRelease) {
      const userId = user?.id || "anon";
      localStorage.setItem(`hostpoint_whats_new_dismissed_${userId}`, latestRelease.version);
    }
    // Also mark release notes as read
    api.post("/release-notes/mark-read").then(() => setReleaseUnreadCount(0)).catch(() => {});
  };

  const handlePropertyChange = (propertyId) => {
    setSelectedPropertyId(propertyId);
    localStorage.setItem("selectedPropertyId", propertyId);
    
    // Filter outlets for this property and select first one
    const propertyOutlets = outlets.filter(o => o.property_id === propertyId);
    if (propertyOutlets.length > 0) {
      setSelectedOutletId(propertyOutlets[0].id);
      localStorage.setItem("selectedOutletId", propertyOutlets[0].id);
    }
  };

  const handleOutletChange = (outletId) => {
    setSelectedOutletId(outletId);
    localStorage.setItem("selectedOutletId", outletId);
  };

  // Get filtered outlets based on selected property
  const filteredOutlets = selectedPropertyId 
    ? outlets.filter(o => o.property_id === selectedPropertyId)
    : outlets;

  const selectedProperty = properties.find(p => p.id === selectedPropertyId);
  const selectedOutlet = outlets.find(o => o.id === selectedOutletId);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const toggleGroup = (title) => {
    setOpenGroups((prev) =>
      prev.includes(title) ? prev.filter((t) => t !== title) : [...prev, title]
    );
  };

  const NavItem = ({ item, mobile }) => {
    if (item.children) {
      return (
        <Collapsible
          open={openGroups.includes(item.title)}
          onOpenChange={() => toggleGroup(item.title)}
        >
          <CollapsibleTrigger asChild>
            <Button
              variant="ghost"
              className="w-full justify-between px-3 py-2 h-auto font-normal"
            >
              <span className="flex items-center gap-3">
                <item.icon className="h-4 w-4" />
                {item.title}
              </span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 transition-transform",
                  openGroups.includes(item.title) && "rotate-180"
                )}
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="pl-7 space-y-1">
            {item.children.map((child) => (
              <NavLink
                key={child.href}
                to={child.href}
                onClick={() => mobile && setSidebarOpen(false)}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )
                }
              >
                <child.icon className="h-4 w-4" />
                {child.title}
              </NavLink>
            ))}
          </CollapsibleContent>
        </Collapsible>
      );
    }

    if (item.external) {
      return (
        <a
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => mobile && setSidebarOpen(false)}
          className="flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          data-testid={`nav-${item.title.toLowerCase().replace(/\s/g, '-')}`}
        >
          <item.icon className="h-4 w-4" />
          {item.title}
          {item.title === "Help & Docs" && releaseUnreadCount > 0 && (
            <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold text-white" data-testid="release-notes-badge">
              {releaseUnreadCount}
            </span>
          )}
        </a>
      );
    }

    return (
      <NavLink
        to={item.href}
        onClick={() => mobile && setSidebarOpen(false)}
        className={({ isActive }) =>
          cn(
            "flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors",
            isActive
              ? "bg-primary/10 text-primary font-medium"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          )
        }
      >
        <item.icon className="h-4 w-4" />
        {item.title}
      </NavLink>
    );
  };

  const Sidebar = ({ mobile }) => (
    <div className="flex flex-col h-full">
      <div className="p-4 border-b">
        <h1 className="text-xl font-bold font-[Manrope] text-primary">HostPoint Retail</h1>
        <p className="text-xs text-muted-foreground mb-3">Admin Panel</p>
        
        {/* Property & Outlet Selector */}
        {!loading && (
          <div className="space-y-2">
            {/* Property Selector */}
            {properties.length > 1 ? (
              <Select value={selectedPropertyId} onValueChange={handlePropertyChange}>
                <SelectTrigger className="w-full h-8 text-xs">
                  <Building2 className="h-3 w-3 mr-1.5 text-muted-foreground" />
                  <SelectValue placeholder="Select Property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map((prop) => (
                    <SelectItem key={prop.id} value={prop.id} className="text-xs">
                      {prop.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : properties.length === 1 ? (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-muted/50 rounded-md">
                <Building2 className="h-3 w-3 text-primary" />
                <span className="text-xs font-medium truncate">{properties[0].name}</span>
              </div>
            ) : null}
            
            {/* Outlet Selector */}
            {filteredOutlets.length > 1 ? (
              <Select value={selectedOutletId} onValueChange={handleOutletChange}>
                <SelectTrigger className="w-full h-8 text-xs">
                  <Store className="h-3 w-3 mr-1.5 text-muted-foreground" />
                  <SelectValue placeholder="Select Outlet" />
                </SelectTrigger>
                <SelectContent>
                  {filteredOutlets.map((outlet) => (
                    <SelectItem key={outlet.id} value={outlet.id} className="text-xs">
                      {outlet.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : filteredOutlets.length === 1 ? (
              <div className="flex items-center gap-1.5 px-2 py-1 bg-muted/50 rounded-md">
                <Store className="h-3 w-3 text-primary" />
                <span className="text-xs font-medium truncate">{filteredOutlets[0].name}</span>
              </div>
            ) : null}
          </div>
        )}
      </div>

      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.map((item) => (
            <NavItem key={item.title} item={item} mobile={mobile} />
          ))}
        </nav>
      </ScrollArea>

      <div className="p-4 border-t space-y-2">
        <Button
          variant="outline"
          className="w-full justify-start"
          onClick={() => navigate("/pos")}
        >
          <Store className="mr-2 h-4 w-4" />
          Open POS
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start text-destructive hover:text-destructive"
          onClick={handleLogout}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Logout
        </Button>
      </div>
    </div>
  );

  // Check if this is a support session
  const isSupportSession = user?.is_support_session;

  const handleExitSupportMode = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Support Session Banner */}
      {isSupportSession && (
        <div className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center justify-between sticky top-0 z-[60]" data-testid="support-session-banner">
          <div className="flex items-center gap-2">
            <Headphones className="h-4 w-4" />
            <span className="text-sm font-medium">
              Support Session: <strong>{user?.support_access_tenant || user?.tenant?.name}</strong>
            </span>
            <Badge variant="outline" className="bg-amber-400/50 border-amber-600 text-amber-950 text-xs">
              All actions are logged
            </Badge>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleExitSupportMode}
            className="bg-amber-400/50 border-amber-600 text-amber-950 hover:bg-amber-400 h-7 text-xs"
            data-testid="exit-support-btn"
          >
            <ArrowLeft className="h-3 w-3 mr-1" />
            Exit Support Mode
          </Button>
        </div>
      )}

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-col fixed inset-y-0 z-50 border-r bg-card">
        <Sidebar mobile={false} />
      </aside>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-background/80 backdrop-blur-sm md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 bg-card border-r transform transition-transform md:hidden",
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        )}
      >
        <Sidebar mobile={true} />
      </aside>

      {/* Main Content */}
      <div className="md:pl-64 flex flex-col min-h-screen">
        {/* Header */}
        <header className="h-16 border-b bg-card px-4 flex items-center justify-between sticky top-0 z-30">
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>

          <div className="flex-1" />

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === "dark" ? (
                <Sun className="h-5 w-5" />
              ) : (
                <Moon className="h-5 w-5" />
              )}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Users className="h-4 w-4" />
                  <span className="hidden sm:inline">{user?.full_name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <div className="px-2 py-1.5 text-sm">
                  <p className="font-medium">{user?.full_name}</p>
                  <p className="text-muted-foreground text-xs">{user?.role?.name}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/admin/settings")}>
                  <Settings className="mr-2 h-4 w-4" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>

      {/* What's New Modal */}
      <Dialog open={whatsNewOpen} onOpenChange={(open) => { if (!open) dismissWhatsNew(); }}>
        <DialogContent className="sm:max-w-lg" data-testid="whats-new-modal">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-1">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <Sparkles className="h-5 w-5 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-xl">What's New</DialogTitle>
                <DialogDescription className="text-xs">
                  {latestRelease?.version && `Version ${latestRelease.version}`}
                  {latestRelease?.released_at && ` — ${new Date(latestRelease.released_at).toLocaleDateString("en-KE", { day: "numeric", month: "long", year: "numeric" })}`}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          {latestRelease && (
            <div className="space-y-4">
              <h3 className="font-semibold text-lg">{latestRelease.title}</h3>
              
              {latestRelease.highlights?.length > 0 && (
                <ul className="space-y-2">
                  {latestRelease.highlights.map((h) => (
                    <li key={h} className="flex items-start gap-2.5 text-sm">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <DialogFooter className="flex-col sm:flex-row gap-2 mt-2">
            <Button variant="outline" onClick={dismissWhatsNew} className="sm:flex-1" data-testid="whats-new-dismiss-btn">
              Got it
            </Button>
            <Button 
              onClick={() => { dismissWhatsNew(); window.open("/help?tab=releases", "_blank"); }} 
              className="sm:flex-1 gap-2"
              data-testid="whats-new-read-more-btn"
            >
              Read Full Notes <ArrowRight className="h-4 w-4" />
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
